import { makeStyles, Typography } from '@material-ui/core';
import FormatQuoteIcon from '@material-ui/icons/FormatQuote';
import {
  boschBlue,
  boschGray,
} from '../../modules/theme/boschColors';

const useStyles = makeStyles(theme => ({
  /*
   * Pull-quote block: a left accent stripe in Bosch Blue, the quote
   * mark icon top-left, and the attribution underneath. The accent
   * stripe deliberately reuses the same Blue 50 used by the section
   * header so a page that mixes both reads as a single visual system.
   */
  root: {
    position: 'relative',
    backgroundColor: '#FFFFFF',
    border: `1px solid ${boschGray[90]}`,
    borderLeft: `4px solid ${boschBlue[50]}`,
    borderRadius: 6,
    padding: theme.spacing(2.5, 3, 2, 3),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  quoteMark: {
    position: 'absolute',
    top: 8,
    right: 16,
    color: boschBlue[90],
    fontSize: 64,
    lineHeight: 1,
    pointerEvents: 'none',
  },
  text: {
    fontSize: '1.05rem',
    color: boschGray[20],
    lineHeight: 1.55,
    fontStyle: 'italic',
    fontWeight: 500,
    /* Keep the quote text from running into the corner quote glyph. */
    paddingRight: theme.spacing(5),
  },
  attribution: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  author: {
    fontSize: '0.85rem',
    fontWeight: 700,
    color: boschGray[15],
  },
  role: {
    fontSize: '0.75rem',
    color: boschGray[40],
  },
}));

export type QuoteProps = {
  text: string;
  author?: string;
  role?: string;
};

/**
 * Styled blockquote with optional attribution. Useful for things like
 * mission statements, principle reminders, or short testimonials on
 * team / governance pages.
 *
 * Intentionally text-only: no inline images, no links. Anything more
 * elaborate (CEO photo + multi-paragraph statement) is a job for the
 * Markdown widget, which we don't want to re-implement here.
 */
export const Quote = ({ text, author, role }: QuoteProps) => {
  const classes = useStyles();
  if (!text) {
    return (
      <div className={classes.root}>
        <Typography className={classes.text}>
          Empty quote — edit and add a statement.
        </Typography>
      </div>
    );
  }
  return (
    <div className={classes.root}>
      <FormatQuoteIcon className={classes.quoteMark} />
      <Typography component="blockquote" className={classes.text}>
        {text}
      </Typography>
      {(author || role) && (
        <div className={classes.attribution}>
          {author && (
            <Typography component="cite" className={classes.author}>
              — {author}
            </Typography>
          )}
          {role && (
            <Typography component="span" className={classes.role}>
              {role}
            </Typography>
          )}
        </div>
      )}
    </div>
  );
};
